/**
 * A marketplace refusing us as automation, and what a run does about it.
 *
 * Poshmark sometimes answers a publish with "We have detected that this item is suspicious. Please
 * use our Single Listing Creation form to list this item." Once it starts saying that to a seller it
 * tends to keep saying it, and nothing in the listing or in Voolist changes the answer. A run that
 * keeps going spends the seller's whole closet finding that out: production has one seller who lost
 * fifteen items to a single run, then re-ran nine smaller batches against the same refusal.
 *
 * We cannot fix Poshmark's side and this does not pretend to. It only stops the spending:
 *   - within a run, the marketplace is dropped after the second refusal and the rest is left alone;
 *   - across runs, the refusal is remembered for a few hours so the next run can warn first.
 *
 * The warning never blocks. We have no way to tell when a refusal lifts, and a seller who has waited
 * it out must still be able to try.
 */

/**
 * How many refusals end the run for that marketplace.
 *
 * One refusal is not a pattern: production shows single rejections inside runs that then carried on
 * succeeding. The second is what separates a one-off from an account the marketplace is refusing,
 * and it is also the point where the seller has lost nothing they would miss.
 */
export const REFUSALS_BEFORE_STOPPING = 2;

/**
 * How long a refusal is worth warning about afterwards.
 *
 * Six hours, chosen from what sellers actually did next. The damage is the same-session reflex:
 * the seller whose run was refused fifteen times started four more batches within ninety minutes,
 * and another seller's whole episode ran under four hours. Six hours covers both and still expires
 * overnight, so someone coming back the next morning is not told about yesterday.
 *
 * Erring short is the safe direction here, because this only warns. If the refusal is still in
 * force, the second-refusal stop above catches it again a few items in.
 */
export const REFUSAL_REMEMBERED_FOR_MS = 6 * 60 * 60 * 1000;

const STORAGE_KEY = 'marketplace_refused_as_automation';

/**
 * The stored fact is deliberately narrow: this marketplace refused us as automation, at this time.
 *
 * It is not per account, because at the point a create fails the browser does not know which
 * account the server picked for it — the marketplace is the whole of what we can honestly say.
 * A seller with two Poshmark accounts is therefore warned about both when either is refused,
 * which is the mistake worth making of the two available.
 */
function readAll() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Storage can be unavailable or hold something we did not write. Either way this is a
    // convenience, never a correctness guard, so it fails quiet rather than taking a run down.
    return {};
  }
}

function writeAll(entries) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* storage full or blocked — the warning is simply not shown next time */
  }
}

/**
 * Write down that a marketplace has just refused us as automation.
 * @param {string} platform
 * @param {number} [now]
 */
export function rememberAutomationRefusal(platform, now = Date.now()) {
  if (!platform) return;
  writeAll({ ...readAll(), [platform]: now });
}

/**
 * When this marketplace last refused us as automation, if that was recent enough to still be worth
 * mentioning. Null once it has aged out, which is also when the entry is dropped.
 *
 * @param {string} platform
 * @param {number} [now]
 * @returns {number|null} the time of the refusal, or null
 */
export function automationRefusedRecently(platform, now = Date.now()) {
  if (!platform) return null;

  const entries = readAll();
  const at = entries[platform];
  if (typeof at !== 'number') return null;

  if (now - at >= REFUSAL_REMEMBERED_FOR_MS) {
    delete entries[platform];
    writeAll(entries);
    return null;
  }

  return at;
}

/**
 * Keeps one run's tally of which marketplaces have refused it as automation.
 *
 * Kept apart from the run loop because it is the whole of the rule, and a rule buried in a loop is
 * one nobody can check. The loop asks it two questions and does the rest: whether a marketplace is
 * already out, and whether the refusal that just arrived is the one that puts it out.
 *
 * The tally belongs to a single run. A new run starts a new one, deliberately: the marketplace may
 * have stopped refusing us in the meantime, and the seller is warned of the risk beforehand rather
 * than being refused by us on the strength of an old answer.
 */
export function createAutomationRefusalTally() {
  const refusalsBy = new Map();
  const refused = new Set();

  return {
    /** Whether this run has already stopped sending this marketplace anything. */
    isRefused: (platform) => refused.has(platform),

    /**
     * Count one refusal.
     * @returns {boolean} true only for the refusal that stops this marketplace, so the caller
     *   reports it once rather than on every refusal after it.
     */
    record: (platform) => {
      if (!platform || refused.has(platform)) return false;

      const refusals = (refusalsBy.get(platform) || 0) + 1;
      refusalsBy.set(platform, refusals);
      if (refusals < REFUSALS_BEFORE_STOPPING) return false;

      refused.add(platform);
      return true;
    },

    /** How many times a marketplace has refused this run. */
    refusalsFor: (platform) => refusalsBy.get(platform) || 0,
  };
}
